// PostHog instrumentation for the Portal Home Page (PROA-1081). Mirrors
// useReportAnalytics: events are snake_case noun_verb, carry org_id/org_name, and
// no-op safely until the AnalyticsProvider/flag enables capture.
//
// HIPAA (per the PostHog Event Schema, Notion): NO PHI in any property. The home
// page renders client names in its lists, but these events deliberately carry
// only report type/status, list position, and URL slugs — never client or learner
// names, report titles, or any free text.
import { useCallback, useRef } from "react";

import { HomeState } from "@pages/HomePage/selectHomeState";
import { useAdminData } from "@providers/AdminDataProvider";
import { useAnalytics } from "@providers/AnalyticsProvider";

// Where a report-open originated within the home page.
type HomeReportSource = "your_reports" | "recently_viewed";

export interface HomeReportClickedProps {
  source: HomeReportSource;
  // Raw report-type / status values — safe (enum-like), never PHI.
  report_type?: string;
  report_status?: string;
  // Zero-based position in the list the user clicked from.
  list_position: number;
}

export function useHomeAnalytics() {
  const analytics = useAnalytics();
  const { orgId, orgName } = useAdminData();

  // home_viewed should fire once per resolved state per mount (and again only if
  // the state genuinely changes, e.g. 1b → 2 after an import).
  const lastViewedState = useRef<HomeState | null>(null);

  const captureHomeViewed = useCallback(
    (home_state: HomeState, has_reports: boolean, has_clients: boolean) => {
      if (!analytics || !orgId) return; // wait for AdminDataProvider to resolve
      if (lastViewedState.current === home_state) return;
      lastViewedState.current = home_state;
      analytics.capture("home_viewed", {
        home_state,
        has_reports,
        has_clients,
        org_id: orgId,
        org_name: orgName,
      });
    },
    [analytics, orgId, orgName],
  );

  const captureStartReportClicked = useCallback(
    (home_state: HomeState) =>
      analytics?.capture("home_start_report_clicked", { home_state, org_id: orgId, org_name: orgName }),
    [analytics, orgId, orgName],
  );

  const captureConnectEhrClicked = useCallback(
    (home_state: HomeState) =>
      analytics?.capture("home_connect_ehr_clicked", { home_state, org_id: orgId, org_name: orgName }),
    [analytics, orgId, orgName],
  );

  const captureImportClientsClicked = useCallback(
    (home_state: HomeState) =>
      analytics?.capture("home_import_clients_clicked", { home_state, org_id: orgId, org_name: orgName }),
    [analytics, orgId, orgName],
  );

  const captureReportClicked = useCallback(
    (props: HomeReportClickedProps) =>
      analytics?.capture("home_report_clicked", { ...props, org_id: orgId, org_name: orgName }),
    [analytics, orgId, orgName],
  );

  const captureReleaseNotesClicked = useCallback(
    (release_slug: string, position: number) =>
      analytics?.capture("home_release_notes_clicked", { release_slug, position, org_id: orgId, org_name: orgName }),
    [analytics, orgId, orgName],
  );

  const captureGuideClicked = useCallback(
    (guide_slug: string, position: number) =>
      analytics?.capture("home_guide_clicked", { guide_slug, position, org_id: orgId, org_name: orgName }),
    [analytics, orgId, orgName],
  );

  return {
    captureHomeViewed,
    captureStartReportClicked,
    captureConnectEhrClicked,
    captureImportClientsClicked,
    captureReportClicked,
    captureReleaseNotesClicked,
    captureGuideClicked,
  };
}
